import React, { createContext, useContext, useState, useEffect } from 'react';
import esTranslations from '../locales/es.json';
import enTranslations from '../locales/en.json';
import frTranslations from '../locales/fr.json';

const allTranslations = {
  es: esTranslations,
  en: enTranslations,
  fr: frTranslations
};

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    if (typeof window === 'undefined') return 'es';
    const saved = localStorage.getItem('language');
    if (saved && allTranslations[saved]) return saved;
    const browser = navigator.language.split('-')[0];
    return allTranslations[browser] ? browser : 'es';
  });
  const [renderKey, setRenderKey] = useState(0);

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.setAttribute('lang', language);
    setRenderKey((prev) => prev + 1);
  }, [language]);

  const changeLanguage = (newLanguage) => {
    if (allTranslations[newLanguage]) setLanguage(newLanguage);
  };

  const t = (key, fallback = '') => {
    const keys = key.split('.');
    let value = allTranslations[language];
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) value = value[k];
      else return fallback || key;
    }
    return typeof value === 'string' ? value : fallback || key;
  };

  const get = (key) => {
    const keys = key.split('.');
    let value = allTranslations[language];
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) value = value[k];
      else return undefined;
    }
    return value;
  };

  const availableLanguages = [
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' }
  ];

  const value = {
    language,
    changeLanguage,
    t,
    get,
    availableLanguages,
    renderKey,
    isRTL: false
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguageContext = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
};
